import { Avatar, Button, Dropdown, Layout, Space, Tooltip } from 'antd';
import {
  IconMenuFold,
  IconMenuUnfold,
  IconResources,
  IconRanking,
} from '../components/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import Clock from '../components/Clock';
import HomeworkNotificationDropdown from './NotificationDropdown';
import { dropdownMenu, RoleIcon } from '../../core/utils/commonUtils';
import { getApiFileUrl } from '../../utils/util';
import { APP_INFO } from '../../core/constants';
import { useBranding, useBrandLogo, useTheme } from '../../core/hooks';
import './layout.css';

const { Header } = Layout;

const HeaderAvatar = ({ user, isConnected, onClick }) => {
  const { t } = useTranslation();
  const initials = `${user?.firstName?.charAt(0) ?? ''}${user?.lastName?.charAt(0) ?? ''}`
    .trim()
    .toUpperCase();

  return (
    <button
      type="button"
      onClick={onClick}
      className="lms-avatar-trigger"
      aria-label={t('common.accountMenu')}
    >
      <Avatar
        size={36}
        src={user?.profilePhotoUrl ? getApiFileUrl(user.profilePhotoUrl) : undefined}
        icon={!user?.profilePhotoUrl && (initials ? null : <RoleIcon role={user?.roleName} />)}
        className="lms-avatar-trigger__avatar"
      >
        {!user?.profilePhotoUrl && initials ? initials : null}
      </Avatar>
      <span
        className={`lms-avatar-trigger__status ${
          isConnected ? 'lms-avatar-trigger__status--online' : 'lms-avatar-trigger__status--offline'
        }`}
        role="img"
        aria-label={isConnected ? t('common.online') : t('common.offline')}
      />
    </button>
  );
};

HeaderAvatar.propTypes = {
  user: PropTypes.object,
  isConnected: PropTypes.bool,
  onClick: PropTypes.func,
};

const AppHeader = ({
  isMobile,
  collapsed,
  onToggleSidebar,
  isConnected,
  notificationCount,
  homeworkNotifications,
  user,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const { logoUrl, logoDarkUrl, centreName } = useBranding();
  const { isDark } = useTheme();
  const brandLogoSrc = useBrandLogo(isDark);
  const hasCentreLogo = Boolean((isDark && logoDarkUrl) || logoUrl);
  const centreLabel = centreName || APP_INFO.NAME;
  const monogram = centreLabel.trim().charAt(0).toUpperCase() || 'L';

  return (
    <Header className="lms-header">
      <div className="lms-header__left">
        {/* Phones have no sidebar to toggle (navigation lives in the bottom
            bar), so the hamburger slot carries the centre's brand instead –
            never the vendor's (W-9). */}
        {isMobile ? (
          <div className="lms-header__brand">
            {hasCentreLogo ? (
              <img src={brandLogoSrc} alt={centreLabel} className="lms-header__brand-logo" />
            ) : (
              <span className="lms-header__brand-monogram" aria-hidden="true">
                {monogram}
              </span>
            )}
            <span className="lms-header__brand-name">{centreLabel}</span>
          </div>
        ) : (
          <Button
            type="text"
            aria-label={collapsed ? t('common.expandSidebar') : t('common.collapseSidebar')}
            icon={collapsed ? <IconMenuUnfold /> : <IconMenuFold />}
            onClick={onToggleSidebar}
            className="lms-header__toggle"
          />
        )}
      </div>

      <div className="lms-header__right">
        <Space size={4} align="center">
          {!isMobile && <Clock />}

          {/* Resources / Ranking move into the bottom-nav "More" sheet on
              mobile – the header has no room for them next to the bell. */}
          {!isMobile && (
            <>
              <Tooltip title={t('navigation.resources')}>
                <Button
                  type="text"
                  icon={<IconResources />}
                  onClick={() => navigate('/resources')}
                  aria-label={t('navigation.resources')}
                  className="lms-header__icon-btn"
                />
              </Tooltip>

              <Tooltip title={t('navigation.ranking')}>
                <Button
                  type="text"
                  icon={<IconRanking />}
                  onClick={() => navigate('/ranking')}
                  aria-label={t('navigation.ranking')}
                  className="lms-header__icon-btn"
                />
              </Tooltip>
            </>
          )}

          <HomeworkNotificationDropdown
            notificationCount={notificationCount}
            homeworkList={homeworkNotifications}
            role={user?.roleName}
          />

          <span className="lms-header__divider" aria-hidden="true" />

          <Dropdown
            menu={{ items: dropdownMenu(navigate, t, { user }) }}
            placement="bottomRight"
            trigger={['click']}
            open={accountDropdownOpen}
            onOpenChange={setAccountDropdownOpen}
            overlayClassName="lms-account-dropdown"
          >
            <span className="lms-avatar-trigger-wrap">
              <HeaderAvatar
                user={user}
                isConnected={isConnected}
              />
            </span>
          </Dropdown>
        </Space>
      </div>
    </Header>
  );
};

AppHeader.propTypes = {
  isMobile: PropTypes.bool,
  collapsed: PropTypes.bool,
  onToggleSidebar: PropTypes.func,
  isConnected: PropTypes.bool,
  notificationCount: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
  homeworkNotifications: PropTypes.array,
  user: PropTypes.object,
};

export default AppHeader;
